import get from 'lodash/get';
import sortBy from 'lodash/sortBy';

import { 
  getContentItemType, 
  getPdfIconUrl, 
  getPptIconUrl, 
  getOtherAssetIconUrl,
  getHtmlPackageIconUrl,
  getCaptionData,
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import {
  NO_STYLE_PRESET_CLASSNAME
} from 'zoomforth_codesbane_viewer/const/style_presets.js';

import {
  LINK_TYPE_INTERNAL,
  LINK_TYPE_EXTERNAL
} from 'zoomforth_codesbane_viewer/const/link_types.js';

import { getDomainFromUrl } from 'zoomforth_zfutils/url_utils.js';

import {
  getAssetForContent,
  getContentItemByUuid,
  getRenderAsMobile,
  getSubpageById,
  getSectionById,
  getCssClassesListForContent
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  getActionTypeNameLegacy
} from 'zoomforth_codesbane_viewer/lib/tile_actions.js';

import {
  getInternalLinkUrl,
  getInternalLinkTarget,
  getExternalLinkUrl,
  getExternalLinkTarget
} from 'zoomforth_codesbane_viewer/lib/app_actions.js';

import {
  isTilePlayable,
  doesTileLinkToVideo
} from 'zoomforth_codesbane_viewer/lib/tile_utils.js';


const hasFileIcon = (contentItem) => {
  const itemType = getContentItemType(contentItem);

  return (
    (
      contentItem.tile_display_type === 'default' && (
        itemType === 'ppt' ||
        itemType === 'pdf' ||
        itemType === 'other'
      ) ||
      // HTML Packages and Iframe Embed tiles should only show a file icon if
      // and only if they open in an overlay and are using the default
      // tile_display_type.
      (
        contentItem.tile_display_type === 'default' &&
        !contentItem.tile_action_data && (
          itemType === 'html_package' ||
          itemType === 'iframe_embed'
        )
      )
    )
  );
};

const hasActionIcon = (state, contentItem) => {
  const cannotShowActionIcon = (
    hasFileIcon(contentItem) ||
    hasVideoIcon(state, contentItem) ||
    getActionTypeNameLegacy(contentItem) === 'no'
  );
  return !cannotShowActionIcon && contentItem.should_show_action_icon;
};

const hasVideoIcon = (state, contentItem) => {
  const itemType = getContentItemType(contentItem);
  return (
    itemType != 'text' && (doesTileLinkToVideo(state, contentItem) || isTilePlayable(contentItem))
  );
};

export const getCroppedImageUrl = (contentItem, asset, sizex, sizey) => {
  const itemType = getContentItemType(contentItem);
  const aspectRatio = '' + sizex + 'x' + sizey;
  const fileIconMap = {
    'pdf': getPdfIconUrl(),
    'html_package': getHtmlPackageIconUrl(),
    'iframe_embed': getHtmlPackageIconUrl(), // we don't have an icon for this but this will work for now
    'ppt': getPptIconUrl(),
    'other': getOtherAssetIconUrl()
  };
  if (hasFileIcon(contentItem)) {
    return fileIconMap[itemType];
  } else if (contentItem.crop_data) {
    if (contentItem.crop_data.dimensions === aspectRatio) {
      // are there any pieces of content in our database that have crop_data.dimensions but not crop_data.url?
      // if not, we can remove the asset.displayImage part -- my hunch is it was there
      // to resolve legacy issues at the time of implementation.
      return contentItem.crop_data.url || asset.displayImage;
    }
  }
  return null;
};

export const getUncroppedImageUrl = (contentItem, asset) => {
  if (contentItem.tile_display_image){
    return contentItem.tile_display_image;
  } else if (asset){
    return asset.displayImage;
  }
  return null;
};

const getLinkImageUrl = (contentItem, asset) => {
  const itemType = getContentItemType(contentItem);
  if (itemType !== 'link') {
    return undefined;
  }
  if (contentItem.tile_display_type === 'scraped_page') {
    return contentItem.link_image;
  } else if (contentItem.tile_display_type === 'library') {
    // NOTE: the aspect ratio for links is always 1x1, so we pass 1 and 1 for sizex and sizey to "getCroppedImageUrl"
    return getCroppedImageUrl(contentItem, asset, 1, 1) || getUncroppedImageUrl(contentItem, asset);
  } else if (contentItem.tile_display_type === 'no_image') {
    return undefined;
  }
};


const getLinkHasNoImage = (contentItem)=> {
  const itemType = getContentItemType(contentItem);
  if (itemType !== 'link') {
    return undefined;
  }
  return contentItem.tile_display_type === 'no_image';
};


const getTileLinkWrappingData = (state, contentItem, asset) => {
  const itemType = getContentItemType(contentItem);
  const tileActionData = contentItem.tile_action_data;
  const subpage = getSubpageById(state, tileActionData && tileActionData.subpageId);
  const section = getSectionById(state, tileActionData && tileActionData.sectionId);
  const isHiddenTargetedSubpage = subpage && subpage.isHidden;
  const isHiddenTargetedSection = section && section.isHidden;

  if (itemType === 'other') {
    return {
      wrappedLinkHref: asset.link,
      wrappedLinkTarget: '_blank',
      shouldShowExternalOpeningMsg: true
    };

  } else if (
    tileActionData && 
    tileActionData.type === LINK_TYPE_EXTERNAL
  ) {
    const externalLinkTarget = getExternalLinkTarget(tileActionData);

    return {
      wrappedLinkHref: getExternalLinkUrl(tileActionData),
      wrappedLinkTarget: externalLinkTarget,
      shouldShowExternalOpeningMsg: externalLinkTarget !== '_parent'
    };

  } else if (
    tileActionData && 
    tileActionData.type === LINK_TYPE_INTERNAL && 
    !isHiddenTargetedSubpage && // Dont create links pointing to hidden subpages
    !isHiddenTargetedSection // Dont create links pointing to hidden sections
  ) {
    return {
      wrappedLinkHref: getInternalLinkUrl(tileActionData),
      wrappedLinkTarget: getInternalLinkTarget(tileActionData)
    };
    
  }
  return {
    wrappedLinkHref: undefined,
    wrappedLinkTarget: undefined
  };
};

export const degenerateTileToGridPosition = ({ uuid, row, col, sizex, sizey })=>
  ({
    uuid,
    row,
    col,
    sizex,
    sizey
  });

export const generateTileFromGridPosition = (state, {uuid, row, col, sizex, sizey})=> {
  const contentItem = getContentItemByUuid(state, uuid);

  // this happens if there is a grid position which does not have an associated content item
  if (!contentItem) {
    return undefined;
  }

  const asset = getAssetForContent(state, contentItem);
  const linkWrapperData = getTileLinkWrappingData(state, contentItem, asset);

  const tileData = {
    uuid,
    row,
    col,
    sizex,
    sizey,
    videoPlayIcon: contentItem.video_play_icon,
    contentId: contentItem.id,
    itemType: getContentItemType(contentItem),
    htmlContent: contentItem.renderedTileCaption,
    asset: asset,
    pageId: contentItem.lensId,
    actionData: contentItem.tile_action_data,
    horizontalAlign: contentItem.horizontal_align ? contentItem.horizontal_align : undefined,
    verticalAlign: contentItem.vertical_align ? contentItem.vertical_align: undefined,
    fontSize: contentItem.font_size,
    stylePresetClassName: contentItem.style_preset_classname || NO_STYLE_PRESET_CLASSNAME,
    cssClassesList: getCssClassesListForContent(state, contentItem),
    linkTitle: contentItem.link_title,
    linkSource: getDomainFromUrl(contentItem.link_canonical_url),
    linkDescription: contentItem.link_description,
    doesTileLinkToVideo: doesTileLinkToVideo(state, contentItem),
    isTilePlayable: isTilePlayable(contentItem),
    isHiddenOnMobile: contentItem.is_hidden_on_mobile || false,
    hasFileIcon: hasFileIcon(contentItem),
    hasActionIcon: hasActionIcon(state, contentItem),
    hasVideoIcon: hasVideoIcon(state, contentItem),
    croppedImageUrl: getCroppedImageUrl(contentItem, asset, sizex, sizey),
    uncroppedImageUrl: getUncroppedImageUrl(contentItem, asset),
    embeddedIframeUrl: contentItem.iframe_embed_url || get(asset, 'entrypointUrl'),
    embeddedIframeTitle: contentItem.iframe_title || '',
    linkImageUrl: getLinkImageUrl(contentItem, asset),
    linkHasNoImage: getLinkHasNoImage(contentItem),
    actionTypeName: getActionTypeNameLegacy(contentItem),
    wrappedLinkHref: linkWrapperData.wrappedLinkHref,
    captionData: getCaptionData(contentItem),
    hoverEffectType: contentItem.hover_effect_type,
    hoverBackgroundColor: contentItem.hover_background_color,
    hoverTextColor: contentItem.hover_text_color,
    wrappedLinkTarget: linkWrapperData.wrappedLinkTarget,
    shouldShowExternalOpeningMsg: linkWrapperData.shouldShowExternalOpeningMsg,
    renderAsMobile: getRenderAsMobile(state),
    tileActionIndicatorIcon: contentItem.tile_action_indicator_icon,
    tile_inner_zf_image: contentItem.tile_inner_zf_image,
    altText: contentItem.alt_text || get(asset, 'alt_text', ''),
    tileTaxonomy: contentItem.tile_taxonomy,
    shouldMaintainStackOrder: get(contentItem, 'should_maintain_stack_order', false),
    animationSettings: contentItem.animationSettings
  };

  return tileData;
};

export const sortTiles = (unsortedTiles)=>
  sortBy(unsortedTiles, ({ row, col })=> {
    return (row * 100) + col; // row # always matters more than column for sorting
  });
